import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const Word = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      style={{ display: "block", willChange: "transform" }}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const wordY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 160]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  void orbY;

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain flex flex-col justify-between pt-32 pb-8"
    >
      {/* intro copy */}
      <div className="relative z-10 px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl">
          <h2 className="text-xl md:text-2xl font-medium tracking-tight leading-[1.25] text-[#0a0a0a]">
            {["We don't just design brands.", "We understand what it takes", "to build one."].map(
              (l, i) => (
                <Word key={i} delay={0.6 + i * 0.12}>
                  {i === 0 ? (
                    l
                  ) : i === 2 ? (
                    <span className="font-serif-accent italic grad-text">{l}</span>
                  ) : (
                    l
                  )}
                </Word>
              )
            )}
          </h2>
        </div>
      </div>

      {/* massive wordmark */}
      <motion.div
        style={{ y: wordY, opacity: fade }}
        className="relative z-10 px-6 md:px-12 lg:px-16"
      >
        <h1
          data-testid="hero-heading"
          className="tight font-extrabold text-[#0a0a0a] text-[19vw] leading-[0.8] select-none"
        >
          <Word delay={0.9}>STUDIO</Word>
          <span className="mask-line">
            <motion.span
              style={{ display: "flex", alignItems: "flex-end", gap: "0" }}
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 1.05 }}
            >
              FRANC
              <motion.svg
                viewBox="0 0 100 100"
                aria-label="O"
                role="img"
                className="w-[13.6vw] mb-[0.4vw] shrink-0"
                initial={{ scale: 0, rotate: -60 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1.6, type: "spring", stiffness: 190, damping: 11 }}
              >
                <motion.g
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ delay: 2.4, duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                  style={{ transformOrigin: "50px 50px" }}
                >
                  <circle cx="50" cy="50" r="49" fill="#0047FF" />
                  <ellipse cx="35" cy="39" rx="4.6" ry="10" fill="#FFD400" />
                  <ellipse cx="65" cy="39" rx="4.6" ry="10" fill="#FFD400" />
                  <path
                    d="M26 58 Q50 86 74 58"
                    stroke="#FFD400"
                    strokeWidth="8.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </motion.g>
              </motion.svg>
            </motion.span>
          </span>
        </h1>
      </motion.div>

      {/* bottom bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 mt-10 flex items-end justify-between border-t border-black/10 px-6 md:px-12 lg:px-16 pt-4"
      >
        <span className="label text-[#0a0a0a]/50">Strategy · Identity · Packaging · Story</span>
        <span className="label hidden md:flex items-center gap-2 text-[#0a0a0a]/50">
          Scroll
          <motion.span
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          >
            {"\u2193\uFE0E"}
          </motion.span>
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
